<template>
    <div>
        <b-alert :message="t('reports::message.realtime_data_info')" type="info"></b-alert>
        <b-datatable
            :key="datatableKey"
            ref="dataTable"
            :columns="columns"
            :endpoint="listRoute"
            :grid-title="gridTitle"
            :hidden-columns="hiddenColumns"
            :initial-filters="initialFilters"
            :keep-filters="true"
            :keep-filters-prefix="initialFilters.report_type"
            has-filter-selector
            @onReset="onReset"
        >
            <template #filters="{ filters }">
                <v-col class="pb-0" cols="3">
                    <b-filter-input v-model="filters.filters.name" :label="t('reports::label.name')"></b-filter-input>
                </v-col>
                <v-col class="pb-0" cols="3">
                    <b-filter-select
                        v-model="filters.filters.pharmacy_site_id"
                        :clearable="filters.filters.pharmacy_site_id !== null"
                        :items="pharmacySitesOptions"
                        :label="t('patients::grid.label.pharmacy_site')"
                        multiple
                    >
                    </b-filter-select>
                </v-col>
                <v-col class="pb-0" cols="3">
                    <b-filter-select
                        v-model="filters.filters.pharmacy_group_id"
                        :clearable="filters.filters.pharmacy_group_id !== null"
                        :items="pharmacyGroupsOptions"
                        :label="t('pharmacy-groups::form.label.pharmacy_group')"
                    >
                    </b-filter-select>
                </v-col>
                <v-col class="pb-0" cols="3">
                    <b-filter-select
                        v-model="filters.filters.consultant_id"
                        :clearable="filters.filters.consultant_id !== null"
                        :items="consultantsOptions"
                        :label="t('patients::grid.label.consultant')"
                        multiple
                    >
                    </b-filter-select>
                </v-col>
                <v-col class="pb-0" cols="3">
                    <b-filter-date
                        v-model="filters.filters.next_due_date"
                        :clearable="filters.filters.next_due_date !== null"
                        :label="t('patients::grid.label.next_due_date')"
                        range
                        :disabled="initialFilters.report_type === 'due_for_the_week'"
                    >
                    </b-filter-date>
                </v-col>
                <v-col class="pb-0" cols="3">
                    <b-filter-select
                        v-model="filters.filters.blood_testing_frequency"
                        :clearable="filters.filters.blood_testing_frequency !== null"
                        :items="filtersOptions.bloodTestingFrequency"
                        :label="t('patients::grid.label.blood_testing_frequency')"
                    >
                    </b-filter-select>
                </v-col>
                <v-col class="pb-0" cols="3">
                    <b-filter-date
                        v-model="filters.filters.frequency_change_date"
                        :clearable="filters.filters.frequency_change_date !== null"
                        :label="t('patients::grid.label.frequency_change_date')"
                        range
                    >
                    </b-filter-date>
                </v-col>
                <v-col v-if="initialFilters.report_type === 'all_patients'" class="pb-0" cols="3">
                    <b-filter-select
                        v-model="filters.filters.is_registered"
                        :items="filtersOptions.is_registered"
                        :label="t('patients::grid.label.is_registered')"
                        :clearable="filters.filters.is_registered !== null"
                    >
                    </b-filter-select>
                </v-col>
            </template>
            <template #buttons>
                <div class="px-2">
                    <v-tooltip top transition="v-fab-transition">
                        <template #activator="{ on: onTooltip, attrs: attrsTooltip }">
                            <v-menu offset-y>
                                <template #activator="{ on, attrs }">
                                    <v-btn
                                        color="primary"
                                        dark
                                        icon
                                        v-bind="{ ...attrs, ...attrsTooltip }"
                                        v-on="{ ...on, ...onTooltip }"
                                    >
                                        <v-icon>mdi-file-export-outline</v-icon>
                                    </v-btn>
                                </template>
                                <v-list dense>
                                    <v-list-item @click="exportRecord('csv')">
                                        <v-list-item-icon>
                                            <v-icon>mdi mdi-file-delimited</v-icon>
                                        </v-list-item-icon>
                                        <v-list-item-title>{{ t('patients::button.export_csv') }}</v-list-item-title>
                                    </v-list-item>
                                    <v-list-item @click="exportRecord('xls')">
                                        <v-list-item-icon>
                                            <v-icon>mdi mdi-file-excel</v-icon>
                                        </v-list-item-icon>
                                        <v-list-item-title>{{ t('patients::button.export_xls') }}</v-list-item-title>
                                    </v-list-item>
                                </v-list>
                            </v-menu>
                        </template>
                        <span>{{ t('patients::button.export') }}</span>
                    </v-tooltip>
                </div>
            </template>

            <template #[`item.name`]="{ item }">
                <div v-if="can('patients.get')" class="link" @click="$root.goTo(editItemUrl(item))">
                    {{ item.name }}
                </div>
                <div v-else>
                    {{ item.name }}
                </div>
            </template>

            <template #[`item.health_status`]="{ item }">
                <div v-if="healthStatus(item)" class="grid-status">
                    <span>
                        {{ healthStatus(item) }}
                    </span>
                </div>
            </template>

            <template #[`item.prohibited`]="{ item }">
                <div class="grid-status">
                    <span v-if="item.prohibited">
                        {{ t('patients::prohibited.true') }}
                    </span>
                    <span v-else>
                        {{ t('patients::prohibited.false') }}
                    </span>
                </div>
            </template>

            <template #[`item.max_dispensing`]="{ item }">
                <div class="grid-status">
                    <span
                        v-text="
                            item.max_dispensing ? t('patients::max_dispensing.yes') : t('patients::max_dispensing.no')
                        "
                    ></span>
                </div>
            </template>

            <template #[`item.has_treatment_break`]="{ item }">
                <div class="grid-status">
                    <span
                        v-text="
                            item.has_treatment_break
                                ? t('patients::treatment_break.true')
                                : t('patients::treatment_break.false')
                        "
                    ></span>
                </div>
            </template>

            <template #[`item.status`]="{ item }">
                {{ t('patients::status.' + item.status) }}
            </template>

            <template #[`item.color`]="{ item }">
                <div v-if="item.color" :class="`blood-status ${item.color}--text`">
                    <div :class="`status ${item.color}--text`">
                        <v-icon>icon icon-blood-result</v-icon>
                    </div>
                    <span v-text="item.color"></span>
                </div>
                <div v-else>
                    <span>{{ t('default::label.na') }}</span>
                </div>
            </template>

            <template #[`item.validity_date`]="{ item }">
                {{ item.validity_date ? moment(item.validity_date).format($root.dateFormat) : t('default::label.na') }}
            </template>

            <template #[`item.is_registered`]="{ item }">
                <div class="grid-status">
                    <span>
                        {{ item.is_registered ? t('default::label.yes') : t('default::label.no') }}
                    </span>
                </div>
            </template>

            <template #[`item.blood_testing_frequency`]="{ item }">
                {{
                    item.blood_testing_frequency
                        ? t('patients::dispensing.form.frequency_items.' + item.blood_testing_frequency)
                        : t('default::label.na')
                }}
            </template>

            <template #[`item.next_due_date`]="{ item }">
                {{ item.next_due_date ? moment(item.next_due_date).format($root.dateFormat) : t('default::label.na') }}
            </template>

            <template #[`item.frequency_change_date`]="{ item }">
                {{
                    item.frequency_change_date
                        ? moment(item.frequency_change_date).format($root.dateFormat)
                        : t('default::label.na')
                }}
            </template>

            <template #[`item.pharmacy_site.pharmacy_groups.group_name`]="{ item }">
                <v-tooltip top transition="v-fab-transition">
                    <template #activator="{ on, attrs }">
                        <span v-bind="attrs" v-on="on">
                            {{
                                item.pharmacy_site?.pharmacy_groups.length
                                    ? item.pharmacy_site.pharmacy_groups.map((entry) => entry.group_name).join(', ')
                                    : t('default::label.na')
                            }}
                        </span>
                    </template>
                    <span>
                        {{
                            item.pharmacy_site?.pharmacy_groups.length
                                ? item.pharmacy_site.pharmacy_groups.map((entry) => entry.group_name).join(', ')
                                : t('default::label.na')
                        }}
                    </span>
                </v-tooltip>
            </template>

            <template #[`item.actions`]="{ item }">
                <div class="table-actions">
                    <v-tooltip
                        v-if="can('api.v1.patients.store') && can('patients.create')"
                        top
                        transition="v-fab-transition"
                    >
                        <template #activator="{ on, attrs }">
                            <v-btn
                                color="disabled-color"
                                icon
                                v-bind="attrs"
                                @click="$root.goTo(editItemUrl(item))"
                                v-on="on"
                            >
                                <v-icon>mdi-pencil</v-icon>
                            </v-btn>
                        </template>
                        <span>{{ t('default::button.edit') }}</span>
                    </v-tooltip>

                    <v-tooltip
                        v-if="!can('api.v1.patients.update') && can('api.v1.patients.show')"
                        top
                        transition="v-fab-transition"
                    >
                        <template #activator="{ on, attrs }">
                            <v-btn
                                color="disabled-color"
                                icon
                                v-bind="attrs"
                                @click="$root.goTo(editItemUrl(item))"
                                v-on="on"
                            >
                                <v-icon>mdi-eye</v-icon>
                            </v-btn>
                        </template>
                        <span>{{ t('default::button.details') }}</span>
                    </v-tooltip>
                </div>
            </template>
        </b-datatable>
    </div>
</template>

<script>
import BAlert from '../../../../../../bgo/bgo-components/resources/js/components/BAlert.vue';
import BFilterDate from '../../../../../../bgo/bgo-components/resources/js/components/BFilterDate.vue';
import BFilterSelect from '../../../../../../bgo/bgo-components/resources/js/components/BFilterSelect.vue';
import _ from 'lodash';

export default {
    components: { BAlert, BFilterSelect, BFilterDate },
    props: {
        listRoute: {
            type: String,
            required: true,
        },
    },
    data() {
        return {
            datatableKey: 0,
            downloadingFile: false,
            hiddenColumns: ['id'],
            columns: [
                {
                    text: t('reports::grid.label.id'),
                    value: 'id',
                    sortable: false,
                    width: 170,
                },
                {
                    text: t('patients::grid.label.blood_status'),
                    value: 'color',
                    sortable: true,
                    width: 120,
                },
                {
                    text: t('patients::grid.label.name'),
                    value: 'name',
                    sortable: true,
                    width: 200,
                },
                {
                    text: t('patients::grid.label.dms_id'),
                    value: 'dms_id',
                    sortable: true,
                    width: 140,
                },
                {
                    text: t('patients::grid.label.nhs_id'),
                    value: 'nhs_id',
                    sortable: true,
                    width: 140,
                },
                {
                    text: t('patients::grid.label.status'),
                    value: 'status',
                    sortable: true,
                    width: 140,
                },
                {
                    text: t('patients::grid.label.validity'),
                    value: 'validity_date',
                    sortable: true,
                    width: 150,
                },
                {
                    text: t('patients::grid.label.pharmacy_site'),
                    value: 'pharmacy_site.site_name',
                    sortable: true,
                    width: 150,
                },
                {
                    text: t('pharmacy-sites::label.pharmacy_groups'),
                    value: 'pharmacy_site.pharmacy_groups.group_name',
                    sortable: true,
                    width: 150,
                },
                {
                    text: t('patients::grid.label.consultant'),
                    value: 'consultant.name',
                    sortable: true,
                    width: 140,
                },
                {
                    text: t('patients::grid.label.blood_testing_frequency'),
                    value: 'blood_testing_frequency',
                    sortable: true,
                    width: 150,
                },
                {
                    text: t('patients::grid.label.next_due_date'),
                    value: 'next_due_date',
                    sortable: true,
                    width: 150,
                },
                {
                    text: t('patients::grid.label.frequency_change_date'),
                    value: 'frequency_change_date',
                    sortable: true,
                    width: 150,
                },
                {
                    text: t('default::grid.label.actions'),
                    value: 'actions',
                    sortable: false,
                    fields: null,
                    width: 200,
                    align: 'end',
                },
            ],
            filtersOptions: {
                treatmentBreakOptions: [
                    {
                        text: t('users::options.status.all'),
                        value: null,
                    },
                    {
                        text: t('patients::treatment_break.true'),
                        value: 1,
                    },
                    {
                        text: t('patients::treatment_break.false'),
                        value: 0,
                    },
                ],
                prohibited: [
                    {
                        text: t('users::options.status.all'),
                        value: null,
                    },
                    {
                        text: t('patients::prohibited.true'),
                        value: 1,
                    },
                    {
                        text: t('patients::prohibited.false'),
                        value: 0,
                    },
                ],
                cnrd_date: [
                    {
                        text: t('patients::options.cnrd_date.all'),
                        value: null,
                    },
                    {
                        text: t('patients::options.cnrd_date.empty_date'),
                        value: 0,
                    },
                    {
                        text: t('patients::options.cnrd_date.not_empty_date'),
                        value: 1,
                    },
                ],
                status: [],
                max_dispensing: [
                    {
                        text: t('users::options.status.all'),
                        value: null,
                    },
                    {
                        text: t('patients::max_dispensing.yes'),
                        value: 1,
                    },
                    {
                        text: t('patients::max_dispensing.no'),
                        value: 0,
                    },
                ],
                validity: [
                    {
                        text: t('users::options.status.all'),
                        value: null,
                    },
                    {
                        text: t('patients::validity.valid'),
                        value: 'valid',
                    },
                    {
                        text: t('patients::validity.invalid'),
                        value: 'invalid',
                    },
                ],
                health_status: [],
                is_registered: [
                    {
                        text: t('users::options.status.all'),
                        value: null,
                    },
                    {
                        text: t('consultants::options.registered.yes'),
                        value: 1,
                    },
                    {
                        text: t('consultants::options.registered.no'),
                        value: 0,
                    },
                ],
                bloodTestingFrequency: [
                    {
                        text: 'Daily',
                        value: 'daily',
                    },
                    {
                        text: 'Weekly',
                        value: 'weekly',
                    },
                    {
                        text: 'Twice Weekly',
                        value: 'twice_weekly',
                    },
                    {
                        text: '2 Weeks',
                        value: '2_weekly',
                    },
                    {
                        text: '4 Weeks',
                        value: '4_weekly',
                    },
                ],
                pharmacySitesOptions: [],
                pharmacyGroupsOptions: [],
                consultantsOptions: [],
            },
        };
    },
    computed: {
        selectedPeriod() {
            return this.$store.state.reports.selectedPeriod;
        },
        canAccessRoute() {
            return this.can('patients.get');
        },
        gridTitle() {
            return this.$store.state.reports.gridTitle;
        },
        selectedPeriodMeta() {
            return this.$store.state.reports.reportChartData.meta.filters;
        },
        initialFilters() {
            return this.$store.state.reports.patientGrid.filters;
        },
        pharmacySitesOptions() {
            return this.$store.state.pharmacy_sites.pharmacySitesOptions.map((x) => ({ value: x.id, text: x.name }));
        },
        pharmacyGroupsOptions() {
            return this.$store.state.pharmacy_groups.pharmacyGroupsOptions.map((x) => ({ value: x.id, text: x.name }));
        },
        consultantsOptions() {
            return this.$store.state.consultants.consultantsOptions.map((x) => ({ value: x.id, text: x.name }));
        },
    },
    watch: {
        selectedPeriod(val) {
            if (this.$refs.dataTable) {
                this.$refs.dataTable.selectedPeriod = val;
                this.$refs.dataTable.applyFilters();
            }
        },
        initialFilters(filters) {
            this.datatableKey++;
            this.$refs.dataTable.filters = filters;
            this.$refs.dataTable.applyFilters();
        },
    },
    created() {
        this.$store.dispatch('pharmacy_sites/loadPharmacySitesOptions');
        this.$store.dispatch('pharmacy_groups/loadPharmacyGroupsOptions');
        this.$store.dispatch('consultants/loadConsultantsOptions');

        axios.get(route('api.v1.patients.config')).then((response) => {
            this.filtersOptions.status = _.union(
                [
                    {
                        text: t('patients::status.all'),
                        value: null,
                    },
                ],
                response.data.statuses
            );

            this.filtersOptions.health_status = _.union(
                [
                    {
                        text: t('patients::status.all'),
                        value: null,
                    },
                ],
                response.data.health_statuses
            );
        });
    },
    methods: {
        async exportRecordAsPdf(item) {
            if (this.downloadingFile) {
                return;
            }

            this.downloadingFile = true;

            return await axios
                .get(route('patients.export-record-as-pdf', { patient: item.id }))
                .then((response) => {
                    const link = document.createElement('a');
                    link.href = response.request.responseURL;
                    document.body.appendChild(link);
                    link.click();
                })
                .catch((error) => {
                    this.$root.notify('error', error.response.data.error);
                })
                .finally(() => {
                    _.delay(() => {
                        this.downloadingFile = false;
                    }, 2000);
                });
        },
        editItemUrl(item) {
            return route('patients.get', { id: item.id });
        },
        healthStatus(item) {
            if (item && item.health_status) {
                return _.map(item.health_status, (status) => {
                    return t('patients::health_status.' + status);
                }).join(', ');
            }
            return false;
        },
        onReset() {},
        async exportRecord(format) {
            const link = document.createElement('a');
            link.href = route(
                'patients.export',
                _.assign({ format: format }, this.$refs.dataTable.filters, {
                    fields: this.$refs.dataTable.fields.join(','),
                })
            );
            document.body.appendChild(link);
            link.click();
        },
    },
};
</script>

<style></style>
